/**
 * SortableTab Component
 * Individual draggable tab for matrix views
 */

import { useState, useEffect, useRef, memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronDown, Lock } from 'lucide-react';

const SortableTab = memo(({
  view,
  isActive,
  onToggleMenu,
  viewContextMenu,
  setActiveViewId,
  setViewContextMenu,
  editingTabId,
  setEditingTabId,
  onRename,
  userProfile
}) => {
  const [editValue, setEditValue] = useState(view.name);
  const inputRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: view.id,
    disabled: view.isDefault || editingTabId === view.id
  });

  // Constrain transform to horizontal axis only
  const constrainedTransform = transform ? {
    ...transform,
    y: 0
  } : null;

  const style = {
    transform: CSS.Transform.toString(constrainedTransform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    marginBottom: isActive ? '-1px' : '0px',
    borderBottomColor: isActive ? 'white' : 'transparent',
    borderBottomWidth: isActive ? '1px' : '0px'
  };

  // Focus input when editing starts
  useEffect(() => {
    if (editingTabId === view.id && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTabId, view.id]);

  const handleSave = async () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== view.name) {
      await onRename(view.id, trimmedValue);
    }
    setEditingTabId(null);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setEditValue(view.name);
      setEditingTabId(null);
    }
  };

  const isEditing = editingTabId === view.id;
  const isOwner = view.userId === userProfile?.id;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="relative">
      <button
        onClick={(e) => {
          if (isEditing) return;

          if (isActive) {
            e.stopPropagation();
            onToggleMenu(e);
          } else {
            setActiveViewId(view.id);
            setViewContextMenu(null);
          }
        }}
        onDoubleClick={(e) => {
          if (isActive && !view.isDefault && isOwner) {
            e.stopPropagation();
            setEditingTabId(view.id);
            setViewContextMenu(null);
          }
        }}
        className={`
          group relative px-4 text-[13px] font-medium transition-all duration-200
          flex items-center gap-1.5 ${isDragging ? 'cursor-grabbing' : 'cursor-pointer'}
          ${isActive
            ? 'bg-white text-gray-900 border-t border-l border-r border-gray-200 rounded-t-lg'
            : 'bg-gray-200/60 text-gray-600 hover:text-gray-900 hover:bg-gray-200 border-t border-l border-r border-transparent rounded-t-md'
          }
        `}
        style={isActive ? {
          zIndex: 20,
          position: 'relative',
          marginBottom: '-1px',
          paddingTop: '0.625rem',
          paddingBottom: 'calc(0.625rem + 1px)'
        } : {
          position: 'relative',
          zIndex: 1,
          paddingTop: '0.5rem',
          paddingBottom: '0.5rem'
        }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            className="relative z-10 bg-transparent border-none outline-none focus:outline-none px-0 min-w-[60px] max-w-[150px]"
            style={{ width: `${Math.max(60, editValue.length * 8)}px` }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <>
            {!isOwner && !view.isDefault && (
              <Lock className="h-3 w-3 text-gray-400" />
            )}
            <span className="relative z-10">{view.name}</span>
          </>
        )}
        {isActive && !isEditing && (
          <ChevronDown className={`relative z-10 h-3.5 w-3.5 text-gray-500 transition-transform duration-200 ${viewContextMenu?.viewId === view.id ? 'rotate-180' : ''}`} />
        )}
      </button>
    </div>
  );
});

SortableTab.displayName = 'SortableTab';

export default SortableTab;
